import { useEffect, useState } from 'react';
import { CaretLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@/components/ui-kit/sidebar';
import { matchesSidebarPath } from '@/lib/utils/sidebar-utils';
import {
  LogoSection,
  SidebarMenuGroupComponent,
  SidebarMenuItemComponent,
} from '@/components/core';
import { usePermittedSidebar } from '@/hooks/use-permitted-sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';

/**
 * AppSidebar — Hrm Tool navigation with a 64px icon rail when collapsed.
 * Collapse via the header caret; expand by clicking the logo mark (no hover flyout).
 *
 * Groups (Attendance, Leave Management, ...) form a single accordion: opening one
 * closes the rest, and the group holding the active route auto-opens on navigation —
 * mirroring the Angular app's FuseNavigationService behavior for the same menu.
 */
export const AppSidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { setOpenMobile, open, setOpen, isMobile, state } = useSidebar();
  const { sections } = usePermittedSidebar();
  const [openGroupTitle, setOpenGroupTitle] = useState<string | null>(null);

  useEffect(() => {
    if (!isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isMobile]);

  useEffect(() => {
    const activeSection = sections.find((section) =>
      section.items.some((item) => matchesSidebarPath(pathname, item.path))
    );
    setOpenGroupTitle(activeSection?.title ?? null);
  }, [pathname, sections]);

  const showText = open || isMobile;
  // Below 1024px the sidebar is a drawer: picking a page closes it.
  const onItemClick = isMobile ? () => setOpenMobile(false) : undefined;
  const isRail = !isMobile && state === 'collapsed';

  return (
    <Sidebar className="h-full border-r border-sidebar-border bg-sidebar" collapsible="icon">
      {isMobile ? (
        <SidebarHeader className="flex h-14 shrink-0 flex-row items-center gap-0 border-b border-border p-0 px-3">
          <LogoSection open={open} isMobile={isMobile} onClose={() => setOpenMobile(false)} />
        </SidebarHeader>
      ) : (
        <SidebarHeader className="flex shrink-0 flex-row items-center gap-1 border-0 p-0 px-2 pb-4 pt-5">
          <LogoSection
            open={open}
            isMobile={false}
            onClose={() => setOpenMobile(false)}
            onExpand={isRail ? () => setOpen(true) : undefined}
          />
          {!isRail && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="nav-toggle ml-auto size-7 shrink-0 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
              aria-controls="app-sidebar"
              aria-expanded
              aria-label={t('COLLAPSE_NAVIGATION')}
              title={t('COLLAPSE_NAVIGATION')}
              onClick={() => setOpen(false)}
            >
              <CaretLeft className="size-[15px]" />
            </Button>
          )}
        </SidebarHeader>
      )}

      <SidebarContent
        id="app-sidebar"
        className={cn(
          'flex flex-col gap-0 overflow-x-hidden py-3 text-sm',
          isRail ? 'px-2' : 'px-3'
        )}
      >
        <SidebarMenu className="gap-1">
          {sections.map((section) =>
            section.icon ? (
              <SidebarMenuGroupComponent
                key={section.title}
                title={section.title}
                icon={section.icon}
                items={section.items}
                showText={showText}
                isRail={isRail}
                isOpen={openGroupTitle === section.title}
                onToggle={() =>
                  setOpenGroupTitle((prev) => (prev === section.title ? null : section.title))
                }
                onItemClick={onItemClick}
              />
            ) : (
              <SidebarMenuItemComponent
                key={section.items[0].id}
                item={section.items[0]}
                showText={showText}
                isActive={pathname.includes(section.items[0].path)}
                onClick={onItemClick}
              />
            )
          )}
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
};
